import type { ReactNode } from "react";
import { Lucide } from "@react-native-vector-icons/lucide";
import { Pressable, StyleSheet, View } from "react-native";

import { AppText } from "./AppText";
import {
  radii,
  spacing,
  useTheme,
  useThemedStyles,
  type ThemeColors,
} from "~/styles/theme";

/**
 * A single line in a list. Rows are borderless and separated by a hairline so a
 * column of them reads as one list inside a parent `Card` — no box-in-box.
 * Pass `first` to the first row to drop its top divider.
 */
export function ListRow({
  title,
  description,
  meta,
  trailing,
  onPress,
  first = false,
  accessibilityLabel,
}: {
  title: ReactNode;
  description?: ReactNode;
  meta?: ReactNode;
  trailing?: ReactNode;
  onPress?: () => void;
  first?: boolean;
  accessibilityLabel?: string;
}) {
  const { colors } = useTheme();
  const stylesheet = useThemedStyles(makeStyles);
  const body = (
    <>
      <View style={stylesheet.main}>
        {typeof title === "string" ? (
          <AppText weight="semibold">{title}</AppText>
        ) : (
          title
        )}
        {description ? (
          typeof description === "string" ? (
            <AppText size="xs" tone="muted">
              {description}
            </AppText>
          ) : (
            description
          )
        ) : null}
        {meta ? <View style={stylesheet.meta}>{meta}</View> : null}
      </View>
      {trailing ? <View style={stylesheet.trailing}>{trailing}</View> : null}
      {onPress ? (
        <Lucide name="chevron-right" size={18} color={colors.mutedForeground} />
      ) : null}
    </>
  );

  if (onPress) {
    return (
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={
          accessibilityLabel ?? (typeof title === "string" ? title : undefined)
        }
        onPress={onPress}
        style={({ hovered, pressed }) => [
          stylesheet.row,
          first ? stylesheet.first : null,
          hovered || pressed ? stylesheet.rowHover : null,
        ]}
      >
        {body}
      </Pressable>
    );
  }

  return (
    <View style={[stylesheet.row, first ? stylesheet.first : null]}>
      {body}
    </View>
  );
}

const makeStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    row: {
      minHeight: 56,
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      gap: spacing.m,
      paddingVertical: spacing.m,
      paddingHorizontal: spacing.s,
      marginHorizontal: -spacing.s,
      borderRadius: radii.s,
      borderTopWidth: 1,
      borderTopColor: colors.border,
    },
    first: { borderTopWidth: 0 },
    rowHover: { backgroundColor: colors.surfaceSunken },
    main: { flex: 1, minWidth: 0, gap: 3 },
    meta: {
      flexDirection: "row",
      flexWrap: "wrap",
      gap: spacing.s,
      marginTop: spacing.xs,
    },
    trailing: {
      flexDirection: "row",
      alignItems: "center",
      gap: spacing.s,
    },
  });
